import { ReactNode, useMemo } from 'react'
import GridLayout, { Layout, WidthProvider } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import { cn } from 'components/UI/cn'
import {
  DashboardTile,
  GRID_COLS,
  GRID_MARGIN,
  GRID_ROW_HEIGHT,
  packTileLayout,
  TileLayout,
} from '../dashboards'
import './dashboard-grid.css'

/** A tile plus the stable id used as its react-grid-layout key. */
export type GridItem = { id: string; tile: DashboardTile }

/** Smallest a tile can be dragged to — keeps charts/headers legible. */
const MIN_W = 2
// 4 rows on the 24px grid ≈ the old 2-row minimum on the 48px grid.
const MIN_H = 4

/** WidthProvider measures the container so the grid is responsive to page width. */
const Grid = WidthProvider(GridLayout)

/**
 * The Retool-style "grid paper" surface shared by the dashboard view and the
 * builder. Tiles are placed on a fixed GRID_COLS grid; when `editable`, they can
 * be dragged to move and resized from the right/bottom/corner handles, snapping
 * to cells. The same component renders read-only (editable=false) on the view
 * page so a saved layout shows exactly as it was arranged.
 *
 * Each item's placement comes from its tile `layout`; tiles without one (shipped
 * dashboards authored with size/height) fall back to packTileLayout.
 *
 * @param items - tiles with their stable grid ids
 * @param editable - enable drag-move + resize (false → static render)
 * @param renderItem - renders a tile's card; receives the item
 * @param onLayoutChange - called with the new {id → layout} map after an edit
 */
export function DashboardGrid({
  items,
  editable,
  renderItem,
  onLayoutChange,
}: {
  items: GridItem[]
  editable: boolean
  renderItem: (item: GridItem) => ReactNode
  onLayoutChange?: (next: Record<string, TileLayout>) => void
}) {
  // Derive the RGL layout from each tile's saved placement, packing those that
  // lack one. Memoized so a stable layout identity doesn't churn RGL each render.
  const layout = useMemo<Layout[]>(() => {
    const packed = packTileLayout(items.map((i) => i.tile))
    return items.map((item, i) => {
      const placed = item.tile.layout ?? packed[i]
      return { i: item.id, ...placed, minW: MIN_W, minH: MIN_H }
    })
  }, [items])

  return (
    <Grid
      className="w-full"
      layout={layout}
      cols={GRID_COLS}
      rowHeight={GRID_ROW_HEIGHT}
      margin={GRID_MARGIN}
      isDraggable={editable}
      isResizable={editable}
      resizeHandles={['s', 'e', 'se']}
      // Header controls (kebab/view picker/remove) opt out of drag via this class.
      draggableCancel=".rgl-no-drag"
      compactType="vertical"
      onLayoutChange={(next) => {
        if (!onLayoutChange) return
        const map: Record<string, TileLayout> = {}
        for (const l of next) map[l.i] = { x: l.x, y: l.y, w: l.w, h: l.h }
        onLayoutChange(map)
      }}
    >
      {items.map((item) => (
        <div
          key={item.id}
          className={cn('overflow-hidden', editable && 'cursor-grab')}
        >
          {renderItem(item)}
        </div>
      ))}
    </Grid>
  )
}
